import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { DashboardLayout } from "./layouts/dashboard/DashboardLayout";
import { billingRoutes } from "./views/admin/billing/routes";
import { ConfigurationView } from "./views/admin/configuration/ConfigurationView";
import { CustomerCreateView } from "./views/admin/customers/CustomerCreateView";
import { CustomerEditView } from "./views/admin/customers/CustomerEditView";
import { CustomersView } from "./views/admin/customers/CustomersView";
import { CustomerView } from "./views/admin/customers/CustomerView";
import { dashboardUsersRoutes } from "./views/admin/dashboard-users/routes";
import { OpenApiProductsView } from "./views/admin/open-api-products/OpenApiProductsView";
import { OpenApiProductView } from "./views/admin/open-api-products/OpenApiProductView";
import { apiCredentialsRedirectRoutes, apiCredentialsRoutes } from "./views/api-credentials/routes";
import { developerToolsRoutes } from "./views/developer-tools/routes";
import { ApiExamplesView } from "./views/ApiExamplesView";
import { CustomerProductsView } from "./views/customer-products/CustomerProductsView";
import { Error404View } from "./views/error/Error404View";
import { RouteErrorView } from "./views/error/RouteErrorView";
import { printAssetsRoutes } from "./views/print-asset/routes";
import { SettingsEditAccountInformationView } from "./views/settings/SettingsEditAccountInformationView";
import { SettingsEditAccountPasswordView } from "./views/settings/SettingsEditAccountPasswordView";
import { SettingsEditAccountView } from "./views/settings/SettingsEditAccountView";
import { SettingsTaxExemptUploadView } from "./views/settings/tax-exempt/SettingsTaxExemptUploadView";
import { SettingsEditOrganizationInformationView } from "./views/settings/account-settings/SettingsEditOrganizationInformationView";
import { SettingsView } from "./views/settings/SettingsView";
import { SettingsAddPaymentMethodView } from "./views/settings/account-settings/SettingsAddPaymentMethodView";
import { SettingsEditPaymentMethodView } from "./views/settings/account-settings/SettingsEditPaymentMethodView";
import { featureFlags } from "./featureFlags";
import { DesignLayout } from "./views/temporary/DesignLayout";
import { DesignView } from "./views/temporary/DesignView";
import { GraphQL } from "@rpi/openapi-api";
import { UploadsView } from "./views/admin/uploads/UploadsView";
import { UploadFilesView } from "./views/admin/uploads/UploadFilesView";
import { UploadsCollectionView } from "./views/admin/uploads/UploadsCollectionView";
import { AuthContainer } from "./components/AuthContainer";
import { AuthenticatorView } from "./views/auth/AuthenticatorView";
import { MantineProvider } from "@mantine/core";
import { rpaTheme } from "./theme/rpaTheme";
import { SetupPayment } from "./views/onBoarding/SetupPayment";
import { OnBoarding } from "./views/onBoarding/OnBoarding";
import { CustomerEditTypeView } from "./views/admin/customers/CustomerEditTypeView";
import { routePaths, withHandle } from "./utils/route.util";
import { productCreateRoute, productDetailsRoute, productEditRoute } from "./views/customer-products/routes";
import { DashboardView } from "./views/dashboard/DashboardView";
import { ordersRoutes } from "./views/orders/routes";
import { IndexRedirect } from "./views/index/IndexRedirect";

export const DASHBOARD_PATH = "/dashboard";
const ADMIN_PATH = "admin";
export const ADMIN_DASHBOARD_PATH = `${DASHBOARD_PATH}/${ADMIN_PATH}`;

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout.Private />,
    errorElement: <RouteErrorView />,
    ...withHandle("app-private"),
    children: [
      {
        path: "onboarding",
        ...withHandle("onboarding-layout"),
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <AuthContainer>
              <Outlet />
            </AuthContainer>
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <OnBoarding />,
            ...withHandle("onboarding"),
          },
          {
            path: "setup-payment",
            element: <SetupPayment />,
            ...withHandle("setup-payment"),
          },
        ],
      },
      {
        path: "dashboard",
        element: <DashboardLayout />,
        ...withHandle("dashboard-layout"),
        children: [
          {
            errorElement: <RouteErrorView root={false} />,
            children: [
              {
                index: true,
                element: <DashboardView />,
                ...withHandle("dashboard"),
              },
              {
                path: "settings",
                element: <SettingsView />,
                ...withHandle("settings"),
                children: [
                  {
                    path: "edit-account",
                    element: featureFlags.newAccountSettings ? (
                      <SettingsEditAccountInformationView />
                    ) : (
                      <SettingsEditAccountView />
                    ),
                    ...withHandle("settings-edit-account"),
                  },
                  {
                    path: "edit-password",
                    element: <SettingsEditAccountPasswordView />,
                    ...withHandle("settings-edit-account"),
                  },
                  ...(featureFlags.newAccountSettings
                    ? [
                        {
                          path: "edit-org",
                          element: <SettingsEditOrganizationInformationView />,
                          ...withHandle("settings-edit-org", {
                            requiredRole: [
                              GraphQL.DashboardUserRole.SuperAdmin,
                              GraphQL.DashboardUserRole.CustomerAdmin,
                            ],
                          }),
                        },
                        {
                          path: "tax-exempt-upload",
                          element: <SettingsTaxExemptUploadView />,
                          ...withHandle("settings-tax-exempt-upload", {
                            requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin],
                          }),
                        },
                      ]
                    : []),
                  {
                    path: "payment-method/add",
                    element: <SettingsAddPaymentMethodView redirect={routePaths.settings} />,
                    ...withHandle("settings-add-payment-method", {
                      requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin],
                    }),
                  },
                  {
                    path: "payment-method/:token/edit",
                    element: <SettingsEditPaymentMethodView redirect={routePaths.settings} />,
                    ...withHandle("settings-edit-payment-method", {
                      requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin],
                    }),
                  },
                ],
              },
              {
                path: "customer-products",
                element: <CustomerProductsView />,
                ...withHandle("customer-products"),
                children: [productCreateRoute, productDetailsRoute, productEditRoute],
              },
              ...printAssetsRoutes,
              ...ordersRoutes,
              ...(featureFlags.developerTools ? apiCredentialsRedirectRoutes : apiCredentialsRoutes),
              ...(featureFlags.developerTools ? developerToolsRoutes : []),
              {
                path: "api-examples",
                element: <ApiExamplesView />,
                ...withHandle("api-examples"),
              },
              {
                path: ADMIN_PATH,
                ...withHandle(ADMIN_PATH, {
                  requiredRole: [GraphQL.DashboardUserRole.SuperAdmin, GraphQL.DashboardUserRole.CustomerAdmin],
                }),
                children: [
                  {
                    path: "customers",
                    element: <CustomersView />,
                    ...withHandle("admin-customers", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    children: [
                      {
                        path: "create",
                        element: <CustomerCreateView />,
                        ...withHandle("admin-customer-create"),
                      },
                    ],
                  },
                  {
                    path: "customers/:customerPublicId",
                    element: <CustomerView />,
                    ...withHandle("admin-customer-detail", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    children: [
                      {
                        path: "edit",
                        element: <CustomerEditView />,
                        ...withHandle("admin-customer-edit"),
                      },
                      {
                        path: "edit-type",
                        element: <CustomerEditTypeView />,
                        ...withHandle("admin-customer-edit-type"),
                      },
                    ],
                  },
                  {
                    path: "open-api-products",
                    ...withHandle("admin-open-api-products", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    element: <OpenApiProductsView />,
                    children: [
                      {
                        path: ":id",
                        element: <OpenApiProductView />,
                        ...withHandle("admin-open-api-product-detail"),
                      },
                    ],
                  },
                  ...dashboardUsersRoutes,
                  ...billingRoutes,
                  {
                    path: "uploads",
                    element: <UploadsView />,
                    ...withHandle("admin-uploads", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    children: [
                      {
                        path: "upload",
                        element: <UploadFilesView />,
                        ...withHandle("admin-upload-files"),
                      },
                    ],
                  },
                  {
                    path: "uploads",
                    ...withHandle("uploads", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    children: [
                      {
                        path: "customers/:customerPublicId/collections/:collectionId",
                        element: <UploadsCollectionView />,
                        ...withHandle("admin-uploads-collection"),
                      },
                    ],
                  },
                  {
                    path: "configuration",
                    ...withHandle("configuration", {
                      requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
                    }),
                    children: [
                      {
                        index: true,
                        element: <ConfigurationView />,
                        ...withHandle("admin-configuration"),
                      },
                    ],
                  },
                ],
              },
              {
                path: "*",
                element: <Error404View root={false} />,
                ...withHandle("not-found"),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/",
    element: <AppLayout.Public />,
    errorElement: <RouteErrorView />,
    ...withHandle("app-public"),
    children: [
      {
        index: true,
        element: <IndexRedirect />,
        ...withHandle("index"),
      },
      {
        path: "auth",
        ...withHandle("auth-layout"),
        element: (
          <MantineProvider theme={rpaTheme} withCSSVariables withGlobalStyles>
            <Outlet />
          </MantineProvider>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="sign-in" replace />,
            ...withHandle("sign-in"),
          },
          {
            path: "sign-in",
            index: true,
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="signIn" />
              </AuthContainer>
            ),
            ...withHandle("sign-in"),
          },
          {
            path: "sign-up",
            element: (
              <AuthContainer>
                <AuthenticatorView initialState="signUp" />
              </AuthContainer>
            ),
            ...withHandle("sign-up"),
          },
          {
            path: "reset-password",
            element: (
              <AuthContainer hideRightPane>
                <AuthenticatorView initialState="resetPassword" />
              </AuthContainer>
            ),
            ...withHandle("reset-password"),
          },
        ],
      },
      {
        path: "design",
        element: <DesignLayout />,
        ...withHandle("design-layout"),
        children: [
          {
            index: true,
            element: <DesignView />,
            ...withHandle("design"),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <Error404View />,
    ...withHandle("not-found"),
  },
]);
